import { formatDate, formatTime, formatNumber } from '@/utils/format';

interface StatusChange {
  status: string;
  timestamp: Date;
}

class FacebookLiveMetadata {
  id: string;

  broadcastId: string;

  pageId: string;

  billingCreditId: string;

  status: string;

  statusHistory: StatusChange[];

  startTime: Date | null;

  endTime: Date | null;

  duration: number | null;

  commentsCount: number;

  commentsProcessed: boolean;

  commentsFetchedAt: Date | null;

  permalink: string;

  createdAt: Date;

  updatedAt: Date;

  constructor({
    id,
    broadcastId,
    pageId,
    billingCreditId,
    status,
    statusHistory,
    startTime,
    endTime,
    duration,
    commentsCount,
    commentsProcessed,
    commentsFetchedAt,
    permalink,
    createdAt,
    updatedAt,
  }: {
    id: string;
    broadcastId: string;
    pageId: string;
    billingCreditId: string;
    status: string;
    statusHistory: StatusChange[];
    startTime: Date | null;
    endTime: Date | null;
    duration: number | null;
    commentsCount: number;
    commentsProcessed: boolean;
    commentsFetchedAt: Date | null;
    permalink: string;
    createdAt: Date;
    updatedAt: Date;
  }) {
    this.id = id;
    this.broadcastId = broadcastId;
    this.pageId = pageId;
    this.billingCreditId = billingCreditId;
    this.status = status;
    this.statusHistory = statusHistory;
    this.startTime = startTime;
    this.endTime = endTime;
    this.duration = duration;
    this.commentsCount = commentsCount;
    this.commentsProcessed = commentsProcessed;
    this.commentsFetchedAt = commentsFetchedAt;
    this.permalink = permalink;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
  }

  getShortId(): string {
    return this.id.substring(0, 16);
  }

  getLiveDate(): string {
    return formatDate(this.createdAt, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  }

  getLiveTime(): string {
    return formatTime(this.createdAt, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formattedCommentsCount(): string {
    return formatNumber(this.commentsCount);
  }

  formattedDuration(): string {
    if (!this.duration) return '-';
    const hours = Math.floor(this.duration / 3600);
    const minutes = Math.floor((this.duration % 3600) / 60);
    const seconds = this.duration % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  isLive(): boolean {
    return this.status === 'live';
  }

  missingPayment(): boolean {
    return !this.billingCreditId;
  }

  getLiveFirstMessageDate(): string {
    return this.getLiveDate();
  }

  getLiveFirstMessageTime(): string {
    return this.getLiveTime();
  }

  get accountId(): string {
    return this.pageId;
  }

  get mediaId(): string {
    return this.broadcastId;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromApiResponse(columns: string[], rows: any[][]): FacebookLiveMetadata[] {
    if (rows.length === 0) {
      return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return rows.map((row: any[]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const v: any = {};
      columns.forEach((column, index) => {
        const camelCaseKey = column.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
        if (row[index] && camelCaseKey === 'Id') {
          v.id = row[index].toString();
        } else if (row[index] && ['updatedAt', 'createdAt', 'startTime', 'endTime', 'commentsFetchedAt'].includes(camelCaseKey)) {
          v[camelCaseKey] = new Date(row[index]);
        } else if (camelCaseKey === 'statusHistory' && row[index]) {
          v[camelCaseKey] = row[index].map((sh: { status: string; timestamp: string }) => ({
            status: sh.status,
            timestamp: new Date(sh.timestamp),
          }));
        } else {
          v[camelCaseKey] = row[index];
        }
      });
      return new FacebookLiveMetadata(v);
    });
  }
}

export default FacebookLiveMetadata;
